import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnDestroy,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { OptionContractInterface } from '@modules/options-module/general/interfaces/options-contract.interface'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { TuiDropdown, TuiDropdownOptionsDirective, TuiLoader } from '@taiga-ui/core'
import { DropdownVariantEnum } from '@shared/enums/dropdown-variant.enum'
import { WidgetNameEnum } from '@modules/widgets-module/enums/widget-name.enum'
import { AssetClassEnum } from '@modules/positions-module/enums/asset-class.enum'
import { OptionsService } from '@modules/options-module/general/services/options.service'
import { OptionsExistsParamsInterface } from '@modules/options-module/general/interfaces/options-exists-params.Interface'
import { Subject, takeUntil } from 'rxjs'
import { GridsterService } from '@shared/services/gridster.service'
import { WidgetsService } from '@modules/widgets-module/services/widgets.service'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { GridsterItemConfig } from 'angular-gridster2'
import { OptionsExistsResponseInterface } from '@modules/options-module/general/interfaces/options-exists-response.Interface'
import { HttpErrorResponse } from '@angular/common/http'
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive'
import { CreateOrderOptionsComponent } from '@shared/components/trading/options-board/common/options-board-table/common/create-order-option/create-order-option.component'
import { OptionsHelperService } from '@modules/options-module/general/services/options-helper.service'
import { AlertService } from '@shared/services/alert.service'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'

@Component({
	selector: 'app-options-board-table-actions',
	templateUrl: './options-board-table-actions.component.html',
	styleUrl: './options-board-table-actions.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TranslatePipe,
		TuiDropdownOptionsDirective,
		TuiDropdown,
		CreateOrderOptionsComponent,
		TuiLoader,
		ClickOutsideDirective,
		ButtonComponent,
	],
})
export class OptionsBoardTableActionsComponent implements OnDestroy {
	protected readonly widgetNameEnum: typeof WidgetNameEnum = WidgetNameEnum
	protected readonly dropdownVariantEnum: typeof DropdownVariantEnum = DropdownVariantEnum
	protected readonly assetClassEnum: typeof AssetClassEnum = AssetClassEnum

	public option: InputSignal<OptionContractInterface | null> = input<OptionContractInterface | null>(null)

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _widgetsService: WidgetsService = inject<WidgetsService>(WidgetsService)
	private readonly _gridsterService: GridsterService = inject<GridsterService>(GridsterService)
	private readonly _optionsService: OptionsService = inject<OptionsService>(OptionsService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _optionsHelperService: OptionsHelperService = inject<OptionsHelperService>(OptionsHelperService)

	protected openedDropdown: WritableSignal<DropdownVariantEnum | null> = signal(null)
	protected isLoadingInfo: WritableSignal<boolean> = signal(false)
	private _dropInfoOption(): void {
		this._optionsHelperService.activeOptionContract$.next(null)

		const widget: WidgetInterface | undefined = this._widgetsService.getWidgetByName(this.widgetNameEnum.info)
		if (!widget) return

		this._widgetsService.updateWidget({
			...widget,
			assetClass: this.assetClassEnum.US_EQUITY,
		})
	}

	private _createInfoWidget(): void {
		const widget: WidgetInterface | undefined = this._widgetsService.getSourceWidget(this.widgetNameEnum.info)
		if (!widget) return
		const widgetSizes: GridsterItemConfig | null = this._gridsterService.calcMaxWidgetSize(widget)
		if (!widgetSizes) {
			this._alertService.warning(this._translateService.instant('addWidget.noSpace'))
			return
		}

		this._widgetsService.createWidget(
			{
				...widget,
				assetClass: this.assetClassEnum.US_OPTION,
			},
			widgetSizes,
		)
	}

	protected openInfoOption(): void {
		const option: OptionContractInterface | null = this.option()
		if (!option) return

		const params: OptionsExistsParamsInterface = {
			symbol: option.underlying_asset.ticker,
			type: option.details.contract_type,
			expiration_date: option.details.expiration_date,
			strike_price: option.details.strike_price,
		}

		this.isLoadingInfo.set(true)
		this._optionsService
			.getOptionsExists(params)
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				(response: OptionsExistsResponseInterface): void => {
					if (!response.option_lists_existence) {
						this._alertService.error(this._translateService.instant('options.errors.notFoundOption'))
						this.isLoadingInfo.set(false)
						return
					}
					this._optionsHelperService.activeOptionContract$.next(option)

					const widget: WidgetInterface | undefined = this._widgetsService.getWidgetByName(
						this.widgetNameEnum.info,
					)

					if (!widget) {
						this._createInfoWidget()
					} else {
						this._widgetsService.updateWidget({
							...widget,
							assetClass: this.assetClassEnum.US_OPTION,
						})
					}
					this.isLoadingInfo.set(false)
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message || this._translateService.instant('options.errors.notFoundOption'),
					)
					this.isLoadingInfo.set(false)
				},
			)
	}

	protected toggleDropdown(dropdown: DropdownVariantEnum | null): void {
		if (this.openedDropdown() === dropdown) {
			this.openedDropdown.set(null)
		} else {
			this.openedDropdown.set(dropdown)
		}
	}

	protected onClickOutside(): void {
		this.openedDropdown.set(null)
	}

	ngOnDestroy(): void {
		this._dropInfoOption()
		this._destroy$.next()
		this._destroy$.complete()
	}
}
